import {
    EdgelessObject,
    normalizeResultScale,
    type IFramework,
    type IResultOptions,
    type TResource,
} from '@/framework';
import {
    TextRenderEvents,
    type ITextRenderer,
    type TextRenderOutput,
    type TextRenderResolution,
    type TextRenderSnapshot,
} from '@/framework/services/text-render';
import { createCanvas, fetchSvgContentWithCache, getVectorTextFromCache, loadImageAsync } from '@/utils/file-kit';
import { rotatePoint } from '@/utils/math-kit';
import { serializeSVG, serializeSVGWithOpacity } from '@/utils/svg-kit';
import type { ILayerProps, IVectorLayerProps } from '@/version-types';
import { Events } from '../constants';
import { buildVectorLayer, getVectorRenderBox, type VectorBuildResult } from '../render';
import type { IEdgelessVectorLayer } from '../types';
import { applyVectorTextLayout, createTextRenderInputFromVectorLayer, requiresSource } from '../utils';

type RuntimeBuild = {
    result: VectorBuildResult;
    layerWidth: number;
    layerHeight: number;
    sourceURL?: string;
};

export class EdgelessVectorLayer extends EdgelessObject implements IEdgelessVectorLayer {
    private readonly textRenderer: ITextRenderer;
    private readonly svgPreviewActiveChangeHandler: number;

    private readonly pendingBuilds = new WeakMap<HTMLImageElement, RuntimeBuild>();

    /**
     * 当前正在生效展示的正式构建结果
     */
    private runtimeBuild: RuntimeBuild | null = null;

    /**
     * SVG preview 是否正在临时接管当前图层。
     */
    private isPreviewActive = false;

    private disposed = false;

    constructor(
        public readonly layerId: string,
        public readonly framework: IFramework,
    ) {
        super(layerId, framework.service);

        this.textRenderer = framework.service.textRender.createRenderer({
            layerId,

            // 结果失效时发布通知，并让普通 API 几何按最新 flatLayer 收敛。
            onInvalidated: () => {
                // Q: 为什么由宿主图层发送 Invalidation，而不是 Renderer 直接 emit？
                // A: Renderer 是不持有事件总线的纯排版引擎（其依赖白名单不含 events），失效出口只是无参回调
                //    「通知我的持有者」。带 layerId 的 framework 级广播属于宿主职责——它本就在总线上，且
                //    Invalidation 的消费者是 Text Editor / SVG Preview 等高层插件，不应让底层引擎反向依赖它们。
                this.service.events.emit(TextRenderEvents.Invalidation, { layerId: this.layerId });

                const layer = this.service.flatLayer.getLayer(this.layerId);
                if (!layer || layer.__isOnTextEditing || !(layer.data as IVectorLayerProps).textInfo) return;

                const latest = this.service.flatLayer.getLayer(this.layerId, true);
                if (!latest) return;

                const resolution = this.layoutText(latest);
                const nextLayer = applyVectorTextLayout(latest, resolution);
                if (nextLayer !== latest) {
                    this.service.frame.addOrUpdateLayer(nextLayer);
                    return;
                }

                // 字体或素材使结果失效时不一定改变逻辑尺寸；即使几何相同，也要重新构建正式 composite，
                // 否则 runtime 会永久保留 pending 前的旧 drawable。
                void this.update(false);
            },
        });

        this.svgPreviewActiveChangeHandler = framework.service.events.on(
            Events.SVGPreviewActiveChange,
            (targetLayerId: string, active: boolean) => {
                if (targetLayerId === this.layerId) this.isPreviewActive = active;
            },
        );
    }

    override shouldSkipRender() {
        return this.isPreviewActive || super.shouldSkipRender();
    }

    override shouldSkipFocusRender() {
        return this.isPreviewActive;
    }

    override supportsCanvasResult() {
        return true;
    }

    override supportsSvgResult() {
        return true;
    }

    /** 带文字的矢量与普通文字同步启动，纯矢量仍使用 rAF */
    override get useRAF(): boolean {
        const layer = this.service.flatLayer.getLayer(this.layerId);
        return !(layer?.data as IVectorLayerProps | undefined)?.textInfo;
    }

    override async loadResource() {
        try {
            const layer = this.service.flatLayer.getLayer(this.layerId);

            // Resize / Scale 开始时只写入操作标记，不应为未变化的 Vector 生成新图片；
            // 交互结束清除标记后仍会按最终图层数据构建正式资源。
            if (layer.__isBasicOperating && this.resource instanceof HTMLImageElement) return this.resource;

            const textBuild = this.resolveTextBuild(layer);

            // pending 不发布失败资源，也不把旧文字与当前图形重新组合。返回同一 drawable
            // 让 EdgelessObject 保持完整旧画面，并避免 active Preview 收到错误的 settled 释放信号；
            // 失效通知之后一定会有新一轮构建，交接等待因此不会永久挂住。
            if (textBuild.status === 'pending') return this.resource as HTMLImageElement | null;

            // 文字终态失败没有后续重建，必须按构建失败上报：EdgelessObject 收到 undefined 时保留
            // 上一份 resource，onResourceSettled 再通知等待交接的 Preview 释放并恢复 Canvas。
            if (textBuild.status === 'failed') return undefined;

            const result = await this.build(layer, { textBuild });
            if (!result) return undefined;

            const image = await loadImageAsync(serializeSVG(result.svg).dataURL);

            this.pendingBuilds.set(image, {
                result,
                layerWidth: layer.width,
                layerHeight: layer.height,
                sourceURL: requiresSource((layer.data as IVectorLayerProps).vectorType)
                    ? (layer.data as IVectorLayerProps).url
                    : undefined,
            });

            return image;
        } catch (error) {
            console.log('EdgelessVectorLayer loadResource error', error);
            return undefined;
        }
    }

    override async getResult({ aiWatermark = false, scale = 1 }: IResultOptions = {}) {
        try {
            const layer = this.service.flatLayer.getLayer(this.layerId);

            const result = await this.build(layer, { waitForText: true });
            if (!result) return {};

            const { dataURL } = serializeSVG(result.svg);
            const svg = serializeSVGWithOpacity(result.svg, layer.opacity).svgText;
            const image = await loadImageAsync(dataURL);

            const normalizedScale = normalizeResultScale(scale);
            const width = Math.max(1, Math.round(result.renderBox.width * normalizedScale));
            const height = Math.max(1, Math.round(result.renderBox.height * normalizedScale));

            const { canvas, ctx } = createCanvas(width, height);

            ctx.drawImage(image, 0, 0, width, height);
            if (aiWatermark) {
                await super.drawAiWatermarkAsync(canvas);
            }
            return { svg, canvas, renderBox: this.getExportBaseInfo(result.renderBox) };
        } catch (error) {
            console.log('EdgelessVectorLayer getResult error', error);
            return {};
        }
    }

    override getBaseInfo() {
        const layer = this.service.flatLayer.getLayer(this.layerId);
        const runtime = this.runtimeBuild;
        const renderBox = runtime?.result.renderBox ?? getVectorRenderBox(layer);
        const scaleX = runtime?.layerWidth ? layer.width / runtime.layerWidth : 1;
        const scaleY = runtime?.layerHeight ? layer.height / runtime.layerHeight : 1;
        const scaledBox = {
            left: renderBox.left * scaleX,
            top: renderBox.top * scaleY,
            width: renderBox.width * scaleX,
            height: renderBox.height * scaleY,
        };

        // 局部 Render Box 左上角绕图层中心旋转，换算成 Edgeless 使用的舞台左上角信息。
        const leftTop = rotatePoint(
            { x: scaledBox.left - layer.width / 2, y: scaledBox.top - layer.height / 2 },
            layer.rotate,
        );

        return {
            left: leftTop.x + layer.left + layer.width / 2,
            top: leftTop.y + layer.top + layer.height / 2,
            width: scaledBox.width,
            height: scaledBox.height,
        };
    }

    /**
     * 获取当前导出数据对应的舞台基础信息。
     *
     * 正式 drawable 在新资源 pending 时会继续使用上一版 Render Box；导出则会基于当前
     * 图层重新构建 SVG。Group 合并必须使用本次导出的 Render Box，避免把新图片放进旧布局。
     *
     * @param renderBox - 已由组合 builder 生成的导出范围。
     * @returns 当前导出 Render Box 在舞台坐标系中的基础信息。
     */
    getExportBaseInfo(renderBox: VectorBuildResult['renderBox']) {
        const layer = this.service.flatLayer.getLayer(this.layerId);
        const leftTop = rotatePoint(
            { x: renderBox.left - layer.width / 2, y: renderBox.top - layer.height / 2 },
            layer.rotate,
        );

        return {
            left: leftTop.x + layer.left + layer.width / 2,
            top: leftTop.y + layer.top + layer.height / 2,
            width: renderBox.width,
            height: renderBox.height,
        };
    }

    /**
     * 提交通过 EdgelessObject 资源版本校验的正式构建结果。
     *
     * preview 只有在最新正式 drawable ready 后才能释放。返回 undefined 表示本轮构建失败，
     * 此时保留旧 drawable 并通知 preview 恢复旧正式画面。
     *
     * @param result - 最新一轮资源加载结果。
     * @returns 无返回值。
     */
    override onResourceSettled(result: TResource): void {
        if (result instanceof HTMLImageElement && this.pendingBuilds.has(result)) {
            this.runtimeBuild = this.pendingBuilds.get(result) ?? null;
            this.pendingBuilds.delete(result);
            this.service.events.emit(Events.DrawableSettled, this.layerId, true);
            return;
        }

        if (result === undefined) {
            this.service.events.emit(Events.DrawableSettled, this.layerId, false);
        }
    }

    override dispose(): void {
        this.disposed = true;
        this.runtimeBuild = null;
        this.framework.service.events.off(this.svgPreviewActiveChangeHandler);
        this.textRenderer.dispose();
        super.dispose();
    }

    /**
     * 同步解析可供 SVG preview 使用的构建结果。
     *
     * Polygon / Graph 只有素材已经由正式渲染加载并进入缓存后才能同步构建；
     * 素材仍在 pending 时返回 null，让正式 Canvas 保持上一份 drawable 或框架占位图。
     *
     * @param layer - 需要预览的最新矢量图层数据。
     * @returns SVG 构建结果；外部素材尚未 ready 或素材无效时返回 null。
     */
    resolveRuntimeSVGResult(layer: ILayerProps): VectorBuildResult | null {
        const props = layer.data as IVectorLayerProps;
        const source = requiresSource(props.vectorType) ? getVectorTextFromCache(props.url) : undefined;

        if (requiresSource(props.vectorType)) {
            // 外部素材文本 ready 早于图片 drawable ready。只有 runtimeBuild 已提交同一个 URL，
            // 才允许 preview 接管，避免首次加载阶段提前隐藏框架占位图。
            if (!source || this.runtimeBuild?.sourceURL !== props.url) return null;
        }

        const textBuild = this.resolveTextBuild(layer);
        if (textBuild.status === 'pending' || textBuild.status === 'failed') return null;

        return buildVectorLayer(layer, source, textBuild);
    }

    /**
     * 等待当前 Vector 完整文字输出后，解析可供导出的 SVG 构建结果。
     *
     * 与同步的 resolveRuntimeSVGResult 对称：该异步入口会先等待字体/素材依赖进入终态，
     * 只交付组合完成的 SVG 与 Render Box，由需要自行栅格化与舞台合成的导出调用方消费；
     * 文字引擎未就绪、宿主已释放、素材或文字失败时返回 null。
     *
     * 文字等待走 `renderAsync()`，不参与画布同步渲染的所有权竞争。
     *
     * @returns 组合完成的 SVG 与 Render Box；无法构建时返回 null。
     */
    async resolveExportSVGResult(): Promise<VectorBuildResult | null> {
        return this.build(this.service.flatLayer.getLayer(this.layerId), { waitForText: true });
    }

    /**
     * 使用真实 Vector 宿主持有的 Renderer 立即排版显式工作值。
     *
     * @param layer - 矢量图层数据。
     * @returns Renderer 的同步四态排版结果；无 textInfo 时返回 empty。
     */
    layoutText(layer: ILayerProps): TextRenderResolution<TextRenderSnapshot> {
        if (!(layer.data as IVectorLayerProps).textInfo) return { status: 'empty' };
        return this.textRenderer.layout(createTextRenderInputFromVectorLayer(layer));
    }

    /**
     * 使用真实 Vector 宿主持有的 Renderer 等待字体依赖后排版显式工作值。
     *
     * 等待只保证本次排版所需字体依赖进入终态，仍保留 ready、empty、pending、failed 四态，
     * 不承诺一定得到 ready 结果。
     *
     * @param layer - 矢量图层数据。
     * @returns 字体依赖准备后的四态排版结果；无 textInfo 时返回 empty。
     */
    async layoutTextAsync(layer: ILayerProps): Promise<TextRenderResolution<TextRenderSnapshot>> {
        if (!(layer.data as IVectorLayerProps).textInfo) return { status: 'empty' };
        return this.textRenderer.layoutAsync(createTextRenderInputFromVectorLayer(layer));
    }

    /**
     * 将当前 Vector 工作值解析成 builder 可消费的文字渲染结果。
     *
     * 直接返回 Renderer 的原始四态：ready/empty 可组合，pending/failed 阻塞组合；
     * 无 textInfo 时同样返回 empty，与只画图形的行为一致。
     *
     * pending 与 failed 必须区分：pending 之后一定会有失效通知触发重建，而 failed 是终态，
     * 等待正式 drawable 交接的 Preview 只能靠 failed 信号释放并恢复上一份有效 Canvas。
     *
     * @param layer - 当前需要组合的 Vector 工作值。
     * @returns Renderer 的原始四态渲染结果；无 textInfo 时返回 empty。
     */
    private resolveTextBuild(layer: ILayerProps): TextRenderResolution<TextRenderOutput> {
        if (!(layer.data as IVectorLayerProps).textInfo) return { status: 'empty' };
        return this.textRenderer.render(createTextRenderInputFromVectorLayer(layer));
    }

    /**
     * 等待当前 Vector 完整文字输出的依赖，再返回 builder 可消费的同输入结果。
     *
     * 该路径直接消费一次 `renderAsync()` 的完整结果，避免先等待排版、再用另一次同步 render
     * 读取到不同输入。仍保留原始四态：`renderAsync()` 是只读等待，不会因画布同步渲染而被取代，
     * pending 只表示文字引擎未就绪或 Renderer 已释放，不能改写为终态 failed。
     *
     * @param layer - 当前显式导出的 Vector 工作值。
     * @returns Renderer 的原始四态渲染结果；无 textInfo 时返回 empty。
     */
    private async resolveTextBuildAsync(layer: ILayerProps): Promise<TextRenderResolution<TextRenderOutput>> {
        if (!(layer.data as IVectorLayerProps).textInfo) return { status: 'empty' };
        return this.textRenderer.renderAsync(createTextRenderInputFromVectorLayer(layer));
    }

    /**
     * 为指定图层构建最终 SVG。
     *
     * @param layer - 需要构建的矢量图层。
     * @param options - 构建选项。
     *  - options.waitForText - 是否等待当前文字依赖进入终态；显式导出传入 true。
     *  - options.textBuild - runtime 已同步解析的文字构建状态，避免同轮重复 render。
     * @returns SVG 构建结果；素材、文字依赖缺失或 SVG 无效时返回 null。
     */
    private async build(
        layer: ILayerProps,
        options: { waitForText?: boolean; textBuild?: TextRenderResolution<TextRenderOutput> } = {},
    ): Promise<VectorBuildResult | null> {
        const props = layer.data as IVectorLayerProps;
        let source: string | undefined;

        if (requiresSource(props.vectorType)) {
            if (!props.url) return null;

            // 素材 SVG 文本由 fetchSvgContentWithCache 在模块级按 URL 缓存并去重，命中时立即返回，
            // 因此这里无需再在图层实例上重复缓存；同步 Preview 路径读取同一份缓存的 getVectorTextFromCache。
            source = await fetchSvgContentWithCache(props.url);

            // fetch 期间图层可能被删除；已销毁的 Edgeless 对象不再继续构建。
            if (this.disposed) return null;
        }

        const textBuild = await (async () => {
            if (options.textBuild) return options.textBuild;
            if (options.waitForText) return this.resolveTextBuildAsync(layer);
            return this.resolveTextBuild(layer);
        })();

        if (textBuild.status === 'pending' || textBuild.status === 'failed') return null;

        return buildVectorLayer(layer, source, textBuild);
    }
}
